'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../../components/DataTable';
import ActionMenu from '../../../../components/ActionMenu';
import { useNotifier } from '../../../../components/Notifier';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';

import TireDamageAddEdit from './TireDamageAddEdit';
import { useLanguage } from '../../../../utils/i18n';
import { useTireDamageDataList, deleteTireDamage, getTireDamageList } from '../../../../stores/tireDamageStore';
import { useCompanyList } from '../../../../stores/companyStore';

function TireDamagePageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'code_internal', label: t('general.code', 'Code') },
      { value: 'name', label: t('general.name', 'Name') },
      { value: 'area', label: t('masterPit.area', 'Area') },
      { value: 'cause', label: t('tireRepairScrap.cause', 'Cause') },
      { value: 'recommend_action', label: t('tireRepairScrap.recommendation', 'Recommend Action') },
      { value: 'description', label: t('general.description', 'Description') }
    ],
    [t]
  );

  const { companyOptions } = useCompanyList();

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const handleCompanyChange = (options) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('error saving company to local storage');
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading,
    refetch
  } = useTireDamageDataList(paginationModel.page, paginationModel.pageSize, selectedCompanies, searchField, searchValue);

  const handleFetchAllForExport = async () => {
    return await getTireDamageList();
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteTireDamage(confirmTargetId);
      notify({ message: t('tireDamage.notifSuccess', 'Damage successfully deleted'), severity: 'success' });
      refetch();
    } catch (err) {
      console.error(err);
      notify({ message: t('tireDamage.notifFailed', 'Failed to delete Damage'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const columns = [
    {
      field: 'company_name',
      headerName: t('general.company', 'Company'),
      flex: 1,
      valueGetter: (params, row) => row?.join?.company_name || row?.company_name || '-'
    },
    { field: 'code_internal', headerName: t('general.code', 'Code'), flex: 0.8 },
    { field: 'name', headerName: t('general.name', 'Name'), flex: 1.5 },
    {
      field: 'area',
      headerName: t('masterPit.area', 'Area'),
      flex: 1,
      valueFormatter: (val) => (val ? val.charAt(0).toUpperCase() + val.slice(1) : '-')
    },
    {
      field: 'cause',
      headerName: t('tireRepairScrap.cause', 'Cause'),
      flex: 1,
      valueFormatter: (val) => (val ? val.charAt(0).toUpperCase() + val.slice(1) : '-')
    },
    {
      field: 'recommend_action',
      headerName: t('tireRepairScrap.recommendation', 'Recommend Action'),
      flex: 1,
      valueFormatter: (val) => (val ? val.charAt(0).toUpperCase() + val.slice(1) : '-')
    },
    { field: 'description', headerName: t('general.description', 'Description'), flex: 1.5 },
    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('general.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('general.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('general.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('general.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('tireDamage.title', 'Master Damage')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('tireDamage.search', 'Search Damage')}
        hideCompanyFilter={false}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      {openDialog && <TireDamageAddEdit open={openDialog} id={editingId} onClose={handleDialogClose} onSuccess={() => refetch()} />}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function TireDamagePage() {
  return <TireDamagePageContent />;
}
